import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Stack,
  Divider,
  Box,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Search as SearchIcon, X as CancelIcon } from 'react-feather';
import AddIcon from '@mui/icons-material/AddOutlined';
import theme from 'src/theme';
import FilterSelectInput from './FilterSelectInput';

const FMSToolbar = ({
  placeholder,
  onSearch,
  searchValue,
  showSearch = true,
  actionName,
  onAction,
  groupLabel,
  groupType,
  groupData,
  showGroup,
  onGroupChange,
  hideAction,
  actionLoading,
  children,
  actionIcon = <AddIcon />,
  sx = {},
}) => {
  const [searchText, setSearchText] = useState('');
  const [curSearch, setCurSearch] = useState('');

  useEffect(() => {
    if (typeof searchValue !== 'string') return;
    setSearchText(searchValue);
    setCurSearch(searchValue);
  }, [searchValue]);

  const onSearchChange = (e) => {
    const searchStr = e.target.value;
    setSearchText(searchStr);
    if (searchStr === '' && curSearch) {
      onSearch('');
    }
  };

  const onClearSearch = () => {
    setSearchText('');
    if (curSearch) {
      setCurSearch('');
      onSearch('');
    }
  };

  const handleSearch = () => {
    setCurSearch(searchText);
    onSearch(searchText);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleGroupChange = (value) => {
    setCurSearch('');
    setSearchText('');
    onGroupChange(value);
  };

  const renderSearchBar = () => (
    <TextField
      size="small"
      placeholder={placeholder}
      value={searchText}
      variant="outlined"
      onChange={onSearchChange}
      onKeyDown={handleKeyDown}
      sx={{
        width: { md: '320px', xs: '100%' },
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          backgroundColor: theme.palette.grey[50],
          border: `1.5px solid ${theme.palette.grey[300]}`,
          transition: 'all 0.3s ease',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {/* Separator */}
            <Box
              sx={{
                width: '1px',
                height: 28,
                bgcolor: theme.palette.divider,
                mx: 1,
              }}
            />
            {/* Search Button */}
            <IconButton onClick={handleSearch}>
              <SearchIcon color={theme.palette.primary.main} size={16} />
            </IconButton>
            {searchText && (
              <IconButton onClick={onClearSearch}>
                <CancelIcon size={16} />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
  return (
    <Card
      sx={{
        borderRadius: 2,
        background: '#FFFFFF',
        boxShadow: 'none',
        overflow: 'hidden',

        border: `1px solid ${theme.palette.divider}`,

        // Gradient border trick

        ...sx
      }}
    >
      {/* Card content goes here */}
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          justifyContent="space-between"
          flexWrap={{ xs: 'nowrap', md: 'wrap', lg: 'nowrap' }}
          useFlexGap
        >
          {/* Left Section */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            flex={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            flexWrap={{ xs: 'nowrap', md: 'wrap', lg: 'nowrap' }}
            useFlexGap
            minWidth={0}
          >
            {showSearch && renderSearchBar()}

            {showGroup && (
            <FilterSelectInput
              label={groupLabel}
              onInputChange={handleGroupChange}
              data={groupData}
              value={groupType}
            />
            )}
            {children}
          </Stack>

          {/* Divider (only on desktop for better separation) */}
          {!hideAction && (
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
          )}

          {/* Action Button */}
          {!hideAction && (
          <Stack sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
            {actionLoading ? (
              <CircularProgress size={28} />
            ) : (
              <Button
                size="large"
                variant="contained"
                startIcon={actionIcon}
                onClick={onAction}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  px: { xs: 2, md: 3 },
                  py: { xs: 1, md: 1.2 },
                  fontWeight: 600,
                  fontSize: 15,
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  },
                }}
              >
                {actionName}
              </Button>
            )}
          </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

FMSToolbar.propTypes = {
  placeholder: PropTypes.string,
  searchValue: PropTypes.string,
  actionName: PropTypes.string,
  groupLabel: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  actionIcon: PropTypes.element,
  sx: PropTypes.object,
  groupType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupData: PropTypes.array,
  showGroup: PropTypes.bool,
  showSearch: PropTypes.bool,
  actionLoading: PropTypes.bool,
  hideAction: PropTypes.bool,
  onSearch: PropTypes.func,
  onAction: PropTypes.func,
  onGroupChange: PropTypes.func,
};

export default FMSToolbar;
